import LicenseRepresentation from "@keycloak/keycloak-admin-client/lib/defs/licenseRepresentation";
import { useEffect, useState } from "react";
import { fetchAdminUI } from "../../context/auth/admin-ui-endpoint";
import { useAdminClient } from "../../admin-client";
import LicenseAppData from "@keycloak/keycloak-admin-client/lib/defs/licenseAppData";
import "./license.css";

interface ErrorMessage {
  message: string;
  success: boolean;
}

export default function DisplayForm() {
  const [data, setData] = useState<LicenseAppData | null>(null); // State to store parsed data
  const [licenseData, setLicenseData] = useState<LicenseRepresentation[]>([]);
  const [error, setError] = useState<ErrorMessage | null>(null);
  const { adminClient } = useAdminClient();

  const [filename, setFilename] = useState<string>("No file chosen"); // State to store the filename
  const [responseStatus, setResponseStatus] = useState<string | null>(null);

  const loader_summary = async () => {
    const summary_data = await fetchAdminUI<LicenseRepresentation>(
      adminClient,
      "ui-ext/license-module",
      {},
    );

    return summary_data;
  };
  useEffect(() => {
    const fetchData = async () => {
      const fetchedData = await loader_summary();
      if (Array.isArray(fetchedData)) {
        setLicenseData(fetchedData); // Set directly if fetchedData is already an array
      } else {
        setLicenseData([fetchedData]); // Wrap fetchedData in an array if it's a single object
      }
      //   Set loading state to false once data is fetched

      console.log("Updated licenseData:", fetchedData);
    };

    fetchData();
  }, []);

  // Handling file input change (optional)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setFilename(file.name); // Update filename state to display in the span

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const fileContent = e.target?.result;
          if (typeof fileContent === "string") {
            // Split the text file content into lines
            const lines = fileContent.split("\n");

            // Initialize an empty object to store parsed data
            const parsedData: Partial<LicenseAppData> = {};

            // Loop through each line and split by the '=' sign
            lines.forEach((line) => {
              const [key, value] = line.split("=").map((part) => part.trim());
              if (key && value) {
                // Populate the parsedData object with the key-value pairs
                parsedData[key as keyof LicenseAppData] = value;
              }
            });

            setData(parsedData as LicenseAppData); // Set the parsed object to state
          }
        } catch (err) {
          setError({
            message: "Error processing the file." + err,
            success: false,
          });
        }
      };
      reader.onerror = () => {
        setError({ message: "Error reading the file.", success: false });
      };

      reader.readAsText(file); // Read the file as text
    }
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault(); // Prevent the form from submitting and reloading the page

    try {
      if (!data) throw new Error("No data to submit");
      const response = await adminClient.licenseInfo.update(data);

      setResponseStatus(response.status);
      if (response.status === "INVALID") {
        setError({
          message: "Invalid license! Please upload a valid license",
          success: false,
        });
        console.error("Error: You have uploaded a invalid license fille.");
      } else if (response.status === "EXPIRED") {
        setError({ message: "The license has been expired.", success: false });
        console.error("Error: The license has been expired.");
      } else if (response.status === "VALID") {
        setError({
          message: "Valid license has been uploaded successfully!",
          success: true,
        });
        console.log("Valid license uploaded.");

        setTimeout(() => {
          window.location.reload(); // Refresh the page
        }, 6000);
      } else {
        setError({
          message: "Unknown license status." + response.status,
          success: false,
        });
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "An unknown error occurred";
      setError({ message: errorMsg, success: false }); // Display error message
      console.error("Error uploading file:", errorMsg);
    }
  };

  // Check if licenseData is available
  if (!licenseData.length) {
    return <div>No License Data Available</div>;
  }

  const licenseItem = licenseData.length > 0 ? licenseData[0] : {};
  const isLicenseItemEmpty = Object.keys(licenseItem).length === 0;

  return (
    <div className="wrapper wrapper--w900">
      <div className="card card-6">
        {/* Display warning message if licenseItem is empty */}
        {isLicenseItemEmpty && (
          <div
            className="warning-message"
            style={{ color: "red", marginBottom: "20px" }}
          >
            <strong>License file does not exist or is invalid!</strong>
          </div>
        )}
        {/* Display warning message if the status is "INVALID" */}
        {licenseItem &&
          licenseItem.status === "INVALID" &&
          responseStatus !== "VALID" && (
            <div
              className="warning-message"
              style={{ color: "red", marginBottom: "20px" }}
            >
              <strong>
                Warning: Invalid license file! Please upload a valid license
                file
              </strong>
            </div>
          )}
        {licenseItem &&
          licenseItem.status === "EXPIRED" &&
          responseStatus !== "VALID" && (
            <div
              className="warning-message"
              style={{ color: "red", marginBottom: "20px" }}
            >
              <strong>Warning: Your License has been expired !</strong>
            </div>
          )}

        <div className="card-heading">
          <h2 className="title">License Information</h2>
        </div>
        <div className="card-body">
          {error && (
            <div
              className="error-message"
              style={{
                color: error.success ? "green" : "red",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              <strong>{error.message}</strong>
            </div>
          )}
          <form className="license-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="name">Serial</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.serial}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">License Issuer</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.licenseIssuer}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">App Name</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.appName}
                  disabled
                />
              </div>
            </div>

            <div className="form-row">
              <div className="name">App Version</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.appVersion}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">MFA</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.mfa}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">External IDP</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.externalIdp}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">App Type</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.appType}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">LayOut Version</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.layOutVersion}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">System MAC</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.systemMAC}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">System IP</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.systemIP}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">Entitled Users</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.entitledUsers}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">Valid Till</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.validTill}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">Issued On</div>
              <div className="value">
                <input
                  className="input--style-6"
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.issuedOn}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="name">License status</div>
              <div className="value">
                <input
                  className={`input--style-6 ${
                    licenseItem.status === "EXPIRED" ||
                    licenseItem.status === "INVALID"
                      ? "text-red"
                      : licenseItem.status === "VALID"
                        ? "text-green"
                        : ""
                  }`}
                  type="text"
                  value={isLicenseItemEmpty ? "NA" : licenseItem.status}
                  disabled
                />
              </div>
            </div>

            <div className="form-row">
              <div className="name">Upload license file</div>
              <div className="value">
                <div className="input-group">
                  <input
                    className="input-file"
                    type="file"
                    name="file_cv"
                    id="file"
                    onChange={handleFileChange}
                    accept=".txt"
                  />
                  <label className="label--file" htmlFor="file">
                    Choose file
                  </label>
                  <span className="input-file__info">{filename}</span>
                </div>
                <div className="label--desc">
                  Upload mPassSSO license file. Should be a text file.
                </div>
              </div>
            </div>

            <div className="card-footer">
              <button className="btn btn--radius-2 btn--blue-2" type="submit">
                Submit
              </button>
            </div>
          </form>
          {/* Conditionally display the error message */}
        </div>
      </div>
    </div>
  );
}
